import { Capacitor } from "@capacitor/core";
import { ColoursObj } from "@eventr/common-storybook";
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react";
import { setStatusBarStyleDark, setStatusBarStyleLight } from "feat/appState/statusBar";
import { applyStatusBarTone, toneOfColour, useStatusBarTone } from "feat/appState/statusBarTone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { routes } from "routing";
import { bs, useBoundStore } from "state/bound.store";
import { useHomeTab } from "state/itineraryState/homeTab.store";
import { HEADER_HEIGHT } from "state/scrollState/scrollContext";
import { useScrollContainerRef } from "state/scrollState/scrollSignals";
import { es, useEphemeralStore } from "state/useEphemeralStore";
import { useTopInset, useWindowStore } from "state/windowState/window.store";
import { useShallow } from "zustand/react/shallow";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { CarouselContainer } from "./CarouselContainer";
import { CarouselFooter } from "./CarouselFooter";
import { CarouselOverlay } from "./CarouselOverlay";
import { CarouselSlide } from "./CarouselSlide";
import { SlideIterator } from "./slide/SlideIterator";
/**
 * A carousel of itineraries in the selected organisation at the top of the
 * Home, Chat, and Team pages. Supports swiping left and right to select an
 * itinerary in the carousel. Each slide has a card which expands to show
 * top-level information about the itinerary (e.g., address, website), as well
 * as location sharing and email booking service functionality.
 *
 * Strategies:
 * - The carousel uses Embla carousel (https://www.embla-carousel.com/).
 * - The carousel removes the top safe area when mounted and restores it when
 *   unmounted.
 * - The carousel adjusts its height based on the selected itinerary and the
 *   open/close state. This logic is based on the auto-height plugin
 *   (https://www.embla-carousel.com/plugins/auto-height/), but **does not use
 *   it**, as it is not extensible with extra dependencies (namely
 *   opening/closing the slide cards)
 *
 * Todo:
 * - Combine the two itinerary endpoints into one to reduce rendering overhead
 *   caused by needing to merge and filter data.
 * @returns The carousel of itineraries.
 */

type CarouselObservable = UseEmblaCarouselType[1];

export default function ItineraryCarousel() {
  const topInset = useTopInset();
  const homeTab = useHomeTab();
  const scrollContainerRef = useScrollContainerRef();
  const itineraryMap = useCachedItineraryMap();

  // MARK: CAROUSEL SETUP
  /**
   * Setting up the carousel.
   *
   * Documentation reference: https://www.embla-carousel.com/get-started/react/#accessing-the-carousel-api
   */

  const startIndex = useMemo(() => {
    if (itineraryMap.length === 0) return 0;

    // Resolved against the same list the slides are rendered from, not the live
    // `bs().itineraryMap`. The rendered list is cached a render behind the store (see
    // useCachedItineraryMap), so on the render where the map changes the two are
    // different arrays and an index taken from one points at a different itinerary in
    // the other.
    //
    // Clamped: an itinerary that is not in the map (including the -1 "none selected"
    // sentinel) gives findIndex -1, which embla reads as the slide before the first one.
    return Math.max(
      itineraryMap.findIndex((itin) => itin?.id === bs().selectedItinID),
      0,
    );
  }, [itineraryMap]);

  const carouselOptions = useMemo(
    () => ({
      loop: true,
      duration: 15,
      watchDrag: itineraryMap.length > 1,
      startIndex: startIndex,
    }),
    [itineraryMap.length, startIndex],
  );

  const [carouselRef, carouselObserver] = useEmblaCarousel(carouselOptions);
  /**
   * Updates the slides in view when the carousel is scrolled. This is used to
   * implement lazy loading for the background images.
   */
  const updateSlidesInView = useCallback((carouselObserver: CarouselObservable) => {
    if (!carouselObserver) return;

    setSlidesInView((slidesInView) => {
      if (slidesInView.length === carouselObserver.slideNodes().length) {
        carouselObserver.off("slidesInView", updateSlidesInView);
      }

      const inView = carouselObserver.slidesInView().filter((index: number) => !slidesInView.includes(index));

      return slidesInView.concat(inView);
    });
  }, []);

  const selectedItinID = useBoundStore((state) => state.selectedItinID);

  // The list the slides on screen were built from, for the "select" handler below to
  // resolve a snap index against. The handler is registered once, so it cannot close
  // over `itineraryMap` without going stale; written after commit so it always names
  // what is actually rendered.
  const renderedMap = useRef(itineraryMap);
  useEffect(() => {
    renderedMap.current = itineraryMap;
  }, [itineraryMap]);

  // Whether the carousel is moving because the user is dragging it. Embla reports a
  // swipe and a programmatic move through the same "select" event, and only a swipe is
  // a new choice of itinerary — see the handler below.
  const fromDrag = useRef(false);
  // Whether that drag's pointer has been lifted, leaving the slide gliding to its snap.
  const released = useRef(false);

  /** Jumps to the store's itinerary if the carousel shows another; see the effect below. */
  const followSelectedItinerary = useCallback((carousel: CarouselObservable) => {
    if (!carousel) return;
    const rendered = renderedMap.current;
    if (!Array.isArray(rendered)) return;
    const selected = bs().selectedItinID;
    if (rendered.at(carousel.selectedScrollSnap())?.id === selected) return;
    const targetIndex = rendered.findIndex((s) => s.id === selected);
    if (targetIndex !== -1) carousel.scrollTo(targetIndex, true);
  }, []);

  // Keeps the carousel in sync when selectedItinID changes from outside a swipe, for
  // example picking an itinerary from the Account itineraries list. Embla only reports
  // swipes through its own "select" event, so an externally set selectedItinID needs
  // this effect to scroll the carousel to match.
  //
  // A jump, not a scroll: on iOS each tab is its own web view rendering its own copy of
  // this header, and embla animates on the frames of the view that owns it. A background
  // tab's frames are paused, so an animated scroll froze part way and played out when the
  // tab came forward — the header sliding sideways onto another itinerary as you changed
  // tab. A jump lands in one frame, whenever that frame comes.
  useEffect(() => {
    if (!carouselObserver) return;
    if (!Array.isArray(itineraryMap)) return;

    const selectedIndex = carouselObserver.selectedScrollSnap();
    const currentItem = itineraryMap.at(selectedIndex);

    if (currentItem?.id !== selectedItinID) {
      const targetIndex = itineraryMap.findIndex((s) => s.id === selectedItinID);
      if (targetIndex !== -1) carouselObserver.scrollTo(targetIndex, true);
    }
  }, [carouselObserver, itineraryMap, selectedItinID]);

  /**
   * Sets up the event listeners for the carousel.
   */
  useEffect(() => {
    if (!carouselObserver) return;

    carouselObserver.on("slidesInView", (carousel) => {
      setSlidesInView(carousel.slidesInView());
    });
    // A drag is the only thing that makes a new choice of itinerary. Embla sets no flag of
    // its own, so bracket it: "pointerDown" opens a drag and "settle" closes it once the
    // carousel has come to rest.
    //
    // "select" also fires for the programmatic jump that follows the store (below) and
    // for the re-init embla-carousel-react performs whenever the options value changes
    // — the itinerary count crossing 1, say. A re-init lands on `startIndex`, captured
    // when the options object was built and by then possibly stale, so committing it
    // would silently move everyone onto a different itinerary: every tab renders its
    // own copy of this header against one shared store. A jump is already following
    // the store, so it has nothing to say back to it either.
    //
    // The choice is committed once the released slide is within a pixel of landing, or on
    // "settle" if no scroll frame gets that close. Embla settles only within 0.001px.
    carouselObserver.on("pointerDown", () => {
      fromDrag.current = true;
      released.current = false;
    });
    // Embla has chosen the snap the slide glides to by "pointerUp". A missing or stale copy
    // of its itinerary starts downloading during the glide.
    carouselObserver.on("pointerUp", (carousel) => {
      released.current = true;
      const landing = renderedMap.current?.at(carousel.selectedScrollSnap())?.id;
      if (landing !== undefined && landing !== bs().selectedItinID) bs().prefetchItinerary(landing);
    });
    // Records the itinerary a drag has brought into the header, until the drag is committed.
    carouselObserver.on("select", (carousel) => {
      if (!fromDrag.current) return;
      es().setItinerarySwipeTarget(renderedMap.current?.at(carousel.selectedScrollSnap())?.id ?? null);
    });
    const commitDrag = (carousel: NonNullable<CarouselObservable>) => {
      if (!fromDrag.current) return;
      fromDrag.current = false;
      // Indexed into the rendered list, not the live one: they differ on the render
      // where the map changes, and the snap index belongs to what is on screen.
      const selected = renderedMap.current?.at(carousel.selectedScrollSnap())?.id ?? -1;
      if (selected !== bs().selectedItinID) bs().setCurrentItin(selected);
      es().setItinerarySwipeTarget(null);
    };
    carouselObserver.on("scroll", (carousel) => {
      if (released.current && hasVisiblyLanded(carousel)) commitDrag(carousel);
    });
    carouselObserver.on("settle", commitDrag);

    // Lazy loading logic to update slides in view.
    updateSlidesInView(carouselObserver);
    carouselObserver.on("slidesInView", updateSlidesInView);

    // A re-init or a resize (folding or unfolding an iPhone Duo, turning the device) can leave
    // the carousel on another slide than the store's itinerary; the jump above only runs when
    // the store changes. Each comes back to it.
    carouselObserver.on("reInit", followSelectedItinerary);
    carouselObserver.on("resize", followSelectedItinerary);

    return function cleanup() {
      carouselObserver.destroy();
      es().setItinerarySwipeTarget(null);
    };
  }, [carouselObserver, updateSlidesInView, followSelectedItinerary]);

  // A tab that comes forward under iOS native navigation shows its own copy of this header,
  // which may have missed a change made in another tab while its frames were paused, or been
  // measured while its view had no size, when every slide sat at 0 and a jump moved nothing.
  // Measured again once the view is on screen, then brought to the store's itinerary.
  const nativeView = useNativeNavigationViewContext();
  useEffect(() => {
    if (!carouselObserver) return;
    return nativeView.addViewDidAppearListener(() => {
      carouselObserver.reInit();
      followSelectedItinerary(carouselObserver);
    });
  }, [nativeView, carouselObserver, followSelectedItinerary]);

  // MARK: SLIDE STATE
  /**
   * The index of selected slide in the carousel. Used to determine the
   * selected itinerary ID.
   */

  // The slide that opens is in view from the first render, so its cover starts loading at once.
  const [slidesInView, setSlidesInView] = useState<number[]>(() => [startIndex]);

  /**
   * Scrolls to the previous slide in the carousel.
   */
  // const scrollPrev = useCallback(() => {
  //     if (carouselObserver) carouselObserver.scrollPrev()
  // }, [carouselObserver])

  // /**
  //  * Scrolls to the next slide in the carousel.
  //  */
  // const scrollNext = useCallback(() => {
  //     if (carouselObserver) carouselObserver.scrollNext()
  // }, [carouselObserver])

  // MARK: HEADER SIZING
  /**
   * Used to find the current URL path, for differentiating between Home,
   * Chat, and Team pages.
   */
  const { pathname } = useLocation();

  /**
   * A map of the slide cards in the carousel, used to calculate the header
   * height. These slide cards can vary in height as itineraries may differ in
   * the amount of information they display.
   */
  // const headerCardReferences = useRef<Map<string, HTMLElement> | null>(null)

  /**
   * Returns the map of slide cards in the carousel.
   */
  // const getHeaderCardMap = useCallback(() => {
  //     // If the map does not exist, create it.
  //     if (!headerCardReferences.current) {
  //         headerCardReferences.current = new Map()
  //     }

  //     // Otherwise, return the map.
  //     return headerCardReferences.current
  // }, [headerCardReferences.current])

  /**
   * Adds or removes a slide card reference to the map of slide cards.
   */
  // const handleSlideCardRef = useCallback(
  //     (node: any, index: any) => {
  //         const map = getHeaderCardMap()

  //         if (node) {
  //             if (map) {
  //                 map.set(index.toString(), node)
  //             }
  //         } else {
  //             if (map) {
  //                 map.delete(index.toString())
  //             }
  //         }
  //     },
  //     [getHeaderCardMap]
  // )

  // MARK: REMOVING TOP SAFE AREA
  // Uses the Mobile App Frame reference to change the grid layout to set the
  // top safe area to 0px (effectively removing it) when the carousel is
  // mounted. On unmount, return the grid layout to the original template.

  const resetTemplate = useCallback(() => {
    const insets = useWindowStore.getState().windowSizes.insets?.insets;
    const components: string[] = [`${insets?.top ?? 0}px`, "1fr", `${insets?.bottom ?? 0}px`];
    if (scrollContainerRef.current?.style) scrollContainerRef.current.style.gridTemplateRows = components.join(" ");
    if (!Capacitor.isNativePlatform()) return;
    if (ColoursObj.navbar.light) {
      setStatusBarStyleDark();
    } else {
      setStatusBarStyleLight();
    }
  }, [scrollContainerRef]);

  const { mapIsFullscreen } = useEphemeralStore(
    useShallow((s) => {
      return {
        mapIsFullscreen: s.mapIsFullscreen,
      };
    }),
  );

  // homeTab is a dep now. Under signals the read subscribed the component but this
  // memo's deps did not include it, so expandMap could serve a stale value when the tab
  // changed without mapIsFullscreen/pathname changing. Matches the identical expression
  // in useIsHeaderVisible below.
  const expandMap = useMemo(() => {
    return mapIsFullscreen && pathname === routes.home.path && homeTab === "map";
  }, [mapIsFullscreen, pathname, homeTab]);

  useEffect(() => {
    if (itineraryMap.length === 0) return;

    if (!scrollContainerRef.current?.style) {
      return;
    }

    const components = scrollContainerRef.current.style.gridTemplateRows?.split(" ");

    components.splice(0, 1, "0px");

    if (expandMap) {
      components.splice(-1, 1, "0px");
    }
    scrollContainerRef.current.style.gridTemplateRows = components.join(" ");

    if (!Capacitor.isNativePlatform()) return;

    // Correct the status bar colour if it is dark. iOS picks it from what is under the bar
    // instead (below).
    if (Capacitor.getPlatform() !== "ios") setStatusBarStyleDark();

    // Resets the grid template rows of the Mobile App Frame component to
    // one with a top safe area when the component unmounts.
    return resetTemplate;
  }, [expandMap, itineraryMap.length, resetTemplate, scrollContainerRef]);

  // MARK: STATUS BAR CONTRAST
  // On iOS the status bar sits over the selected itinerary's picture while the header is in
  // view, and over the page's strip once it has faded off (see HomeScrollAwayContent). Its
  // text takes whichever of light or dark reads against that. A picture not yet sampled
  // goes by the header colour it is painted over.
  const imageTone = useStatusBarTone((s) => s.imageTones[String(selectedItinID)]);
  const headerUnderBar = useStatusBarTone((s) => s.headerUnderBar);
  const showing = !expandMap && itineraryMap.length > 0;
  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios" || !showing) return;
    applyStatusBarTone(
      headerUnderBar
        ? (imageTone ?? toneOfColour(ColoursObj.backgrounds.header))
        : toneOfColour(ColoursObj.backgrounds.primary),
    );
  }, [headerUnderBar, imageTone, showing]);

  if (expandMap) return null;
  if (itineraryMap.length === 0) return null;

  return (
    // The overlay buttons live inside the container so they travel with the header when it
    // scrolls away; the container is fixed at the top of the screen, so their offsets are
    // measured from the same place either way.
    <CarouselContainer>
      <CarouselOverlay />
      <div
        style={{
          // Wider by the vertical bar beside it, if any (see CarouselContainer).
          width: "calc(var(--app-width) + var(--header-bleed-left, 0px) + var(--header-bleed-right, 0px))",
          overflow: "hidden",
          height: HEADER_HEIGHT + topInset,
        }}
        ref={carouselRef}
      >
        <SlideIterator>
          {itineraryMap.map((source, index) => (
            <CarouselSlide key={index} itineraryMap={source} slidesInView={slidesInView} index={index} />
          ))}
        </SlideIterator>
      </div>
      <CarouselFooter />
    </CarouselContainer>
  );
}

/** Whether the carousel is within a pixel of the snap it is gliding to. */
function hasVisiblyLanded(carousel: NonNullable<CarouselObservable>): boolean {
  const { target, offsetLocation } = carousel.internalEngine();
  return Math.abs(target.get() - offsetLocation.get()) < 1;
}

function useCachedItineraryMap() {
  const itineraryMap = useBoundStore((state) => state.itineraryMap);
  const [cachedMap, setCachedMap] = useState(itineraryMap);

  useEffect(() => {
    if (itineraryMap !== cachedMap) {
      setCachedMap(itineraryMap);
    }
  }, [itineraryMap, cachedMap]);

  return cachedMap;
}

/**
 * Whether ItineraryCarousel is actually rendering its header. Mirrors its own
 * `if (expandMap) return null; if (itineraryMap.length === 0) return null;` gate.
 * HomeRoutes uses this to size the main content's top offset, since the carousel
 * is a fixed overlay and its height doesn't take up document flow space.
 */
export function useIsHeaderVisible(): boolean {
  const homeTab = useHomeTab();
  const itineraryMap = useCachedItineraryMap();
  const { mapIsFullscreen } = useEphemeralStore(
    useShallow((s) => ({
      mapIsFullscreen: s.mapIsFullscreen,
    })),
  );
  const { pathname } = useLocation();
  const expandMap = mapIsFullscreen && pathname === routes.home.path && homeTab === "map";
  return itineraryMap.length > 0 && !expandMap;
}
